import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Send, Trash2, Eye, Settings as SettingsIcon, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

export default function AdminLeadCenter() {
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({
    email_notifications: false,
    notification_emails: [],
    webhook_url: '',
    webhook_enabled: false
  });
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeads();
    fetchSettings();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await apiClient.get('/admin/leads');
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/lead-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put('/admin/lead-settings', settings);
      toast.success('Lead settings saved!');
      setIsSettingsModalOpen(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleAddEmail = () => {
    if (newEmail && newEmail.includes('@')) {
      setSettings({
        ...settings,
        notification_emails: [...settings.notification_emails, newEmail]
      });
      setNewEmail('');
    } else {
      toast.error('Please enter a valid email');
    }
  };

  const handleRemoveEmail = (email) => {
    setSettings({
      ...settings,
      notification_emails: settings.notification_emails.filter(e => e !== email)
    });
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setIsViewModalOpen(true);
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await apiClient.delete(`/admin/leads/${leadId}`);
        toast.success('Lead deleted!');
        fetchLeads();
      } catch (error) {
        toast.error('Failed to delete lead');
      }
    }
  };

  const handleSendEmail = async (leadId) => {
    try {
      const response = await apiClient.post(`/admin/leads/${leadId}/send-email`);
      toast.success(response.data.message || 'Email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleSendWebhook = async (leadId) => {
    try {
      const response = await apiClient.post(`/admin/leads/${leadId}/send-webhook`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send webhook');
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await apiClient.get('/admin/leads/export/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Leads exported successfully!');
    } catch (error) {
      toast.error('Failed to export leads');
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading leads...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Lead Center</h1>
              <p className="text-gray-600 mt-1">Manage enquiries and configure email/webhook notifications</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleDownloadExcel} className="bg-green-600 hover:bg-green-700" data-testid="download-leads-btn">
                <Download size={18} className="mr-2" />
                Download Excel
              </Button>
              <Button onClick={() => setIsSettingsModalOpen(true)} className="bg-gray-700 hover:bg-gray-800">
                <SettingsIcon size={18} className="mr-2" />
                Settings
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-gray-900">{leads.length}</div>
            <div className="text-gray-600">Total Leads</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-green-600">
              {settings.email_notifications ? 'Enabled' : 'Disabled'}
            </div>
            <div className="text-gray-600">Email Notifications</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-blue-600">
              {settings.webhook_enabled ? 'Active' : 'Inactive'}
            </div>
            <div className="text-gray-600">Webhook Status</div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No leads yet. Enquiries will appear here.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(lead.submitted_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lead.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.workspace_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleViewLead(lead)}>
                            <Eye size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleSendEmail(lead.id)}>
                            <Mail size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleSendWebhook(lead.id)}>
                            <Send size={16} />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteLead(lead.id)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Lead Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Name</Label>
                  <p className="font-semibold">{selectedLead.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <p className="font-semibold">{selectedLead.email}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Phone</Label>
                  <p className="font-semibold">{selectedLead.phone}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Location</Label>
                  <p className="font-semibold">{selectedLead.location}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Workspace Type</Label>
                  <p className="font-semibold">{selectedLead.workspace_type}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Seats Required</Label>
                  <p className="font-semibold">{selectedLead.seats_required}</p>
                </div>
                {selectedLead.company_name && (
                  <div className="col-span-2">
                    <Label className="text-gray-600">Company</Label>
                    <p className="font-semibold">{selectedLead.company_name}</p>
                  </div>
                )}
                {selectedLead.message && (
                  <div className="col-span-2">
                    <Label className="text-gray-600">Message</Label>
                    <p className="font-semibold">{selectedLead.message}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-gray-600">Submitted At</Label>
                  <p className="font-semibold">{formatDate(selectedLead.submitted_at)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={isSettingsModalOpen} onOpenChange={setIsSettingsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lead Center Settings</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSettings} className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Email Notifications</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.email_notifications}
                    onChange={(e) => setSettings({ ...settings, email_notifications: e.target.checked })}
                    className="rounded"
                  />
                  <Label>Enable email notifications for new leads</Label>
                </div>
                <div>
                  <Label>Notification Emails</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="email@example.com"
                      type="email"
                    />
                    <Button type="button" onClick={handleAddEmail}>
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {settings.notification_emails.map((email, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {email}
                        <button type="button" onClick={() => handleRemoveEmail(email)} className="hover:text-blue-900">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Note: Email service integration required for sending emails</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-3">Webhook Integration</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.webhook_enabled}
                    onChange={(e) => setSettings({ ...settings, webhook_enabled: e.target.checked })}
                    className="rounded"
                  />
                  <Label>Enable webhook for CRM integration</Label>
                </div>
                <div>
                  <Label>Webhook URL</Label>
                  <Input
                    value={settings.webhook_url}
                    onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                    placeholder="https://your-crm.com/webhook/leads"
                    type="url"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leads will be sent to this URL as JSON POST requests</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsSettingsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Save Settings
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}